import { Group, Stack } from '@mantine/core';
import type { DataAccessRequestOut } from '@repo/api-codegen';
import MultiSelectorTarget from '@repo/common/components/MultiSelector/MultiSelectorTarget';
import type { EntityType } from '@repo/common/enums/entityType';
import { Text, Title } from '@repo/foundations';
import { useMemo } from 'react';
import { useIntegrationList } from '../../api';
import { DateRenderer } from '../../components/CatalogView/TableView/DateRenderer';
import { DataAccessRequestIcon } from '../../components/DataAccessRequest/DataAccessRequestIcon/DataAccessRequestIcon';
import { DataAccessRequestStatusBadge } from '../../components/DataAccessRequest/DataAccessRequestStatusBadge/DataAccessRequestStatusBadge';
import { SecodaEntityIcon } from '../../components/SecodaEntity';
import {
	CreatedAtRender,
	LastUpdatedRender,
} from '../../components/TableV2/render';
import type { ExtendedDataTableColumn } from '../../components/TableV2/types';

export function useColumns(): ExtendedDataTableColumn<DataAccessRequestOut>[] {
	const { data: integrations } = useIntegrationList({});

	const columns = useMemo(
		() => [
			{
				accessor: 'created_by',
				titleLabel: 'Request',
				sortable: false,
				render: (record: DataAccessRequestOut) => {
					const integration = integrations?.results.find(
						(item) => item.id === record.integration_id
					);

					return (
						<Group role="link" spacing="sm" noWrap>
							<DataAccessRequestIcon
								user={record.created_by}
								status={record.status}
							/>
							<Stack spacing={0}>
								<Title size="sm" truncate>
									{integration?.name} access
								</Title>
								<Text size="xs" color="text/secondary/default">
									{record.created_by?.display_name} ·{' '}
									<DateRenderer
										value={record.created_at}
										relative
										color="text/secondary/default"
										size="xs"
										span
									/>
								</Text>
							</Stack>
						</Group>
					);
				},
			},
			{
				accessor: 'requested_resources',
				titleLabel: 'Resources',
				render: (record: DataAccessRequestOut) => (
					<MultiSelectorTarget
						selected={(record.requested_resources ?? [])?.map((resource) => ({
							label: resource.title || 'Untitled',
							value: resource.id,
							icon: (
								<SecodaEntityIcon
									entity={{
										...resource,
										integration: resource.integration_id ?? undefined,
										entity_type: resource.entity_type as EntityType,
										icon: resource.icon || undefined,
									}}
									size={20}
								/>
							),
						}))}
						iconType="react-node"
						property="entity"
						propertyLabel="resources"
						isMenuItemBadge={false}
						isViewerUser={false}
						permittedId={''}
						readOnly
						hideOnEmpty
					/>
				),
			},
			{
				accessor: 'requested_text',
				titleLabel: 'Request comment',
				render: (record: DataAccessRequestOut) => (
					<Text
						size="sm"
						color="text/primary/default"
						style={{
							overflow: 'hidden',
							textOverflow: 'ellipsis',
							whiteSpace: 'nowrap',
							maxWidth: '300px',
						}}
					>
						{record.requested_text || ''}
					</Text>
				),
			},
			{
				accessor: 'approved_text',
				titleLabel: 'Approval comment',
				render: (record: DataAccessRequestOut) => (
					<Text
						size="sm"
						color="text/primary/default"
						style={{
							overflow: 'hidden',
							textOverflow: 'ellipsis',
							whiteSpace: 'nowrap',
							maxWidth: '300px',
						}}
					>
						{record.approved_text || ''}
					</Text>
				),
			},
			{
				accessor: 'rejected_text',
				titleLabel: 'Rejection comment',
				render: (record: DataAccessRequestOut) => (
					<Text
						size="sm"
						color="text/primary/default"
						style={{
							overflow: 'hidden',
							textOverflow: 'ellipsis',
							whiteSpace: 'nowrap',
							maxWidth: '300px',
						}}
					>
						{record.rejected_text || ''}
					</Text>
				),
			},
			{
				accessor: 'requested_expires_at',
				titleLabel: 'Access end date',
				render: (record: DataAccessRequestOut) =>
					record.requested_expires_at || record.approved_expires_at ? (
						<DateRenderer
							value={
								record.approved_expires_at ??
								record.requested_expires_at ??
								undefined
							}
							color="text/primary/default"
							size="xs"
							span
						/>
					) : (
						<Text size="xs">Indefinite</Text>
					),
			},
			{
				accessor: 'status',
				titleLabel: 'Status',
				width: 120,
				render: (record: DataAccessRequestOut) => (
					<DataAccessRequestStatusBadge status={record.status} />
				),
			},
			{
				accessor: 'created_at',
				titleLabel: 'Created at',
				render: (record: DataAccessRequestOut) => (
					<CreatedAtRender record={record} />
				),
			},
			{
				accessor: 'updated_at',
				titleLabel: 'Last updated',
				render: (record) => (
					<LastUpdatedRender record={record} field={'updated_at'} />
				),
			},
		],
		[integrations]
	);

	return columns;
}
